import { districts, provinces, regencies, villages } from '@script/address'

/**
 * Retrieves all provinces in the database.
 *
 * @param _countryId - Country identifier (currently unused, reserved for future filtering)
 * @returns Array of all available provinces
 *
 * @remarks
 * Currently returns all provinces regardless of country.
 * Future implementation will filter provinces by the provided country ID.
 *
 * @example
 * const indonesianProvinces = getProvinces('ID')
 * console.log(indonesianProvinces.length) // e.g., 34
 *
 * @todo Implement province filtering by country ID
 */
export function getProvinces(_countryId: string) {
  return provinces
}

/**
 * Retrieves all regencies (kabupaten/kota) within a specific province.
 *
 * @param provinceId - The unique identifier of the parent province
 * @returns Array of regencies belonging to the specified province
 *
 * @remarks
 * In Indonesian administrative hierarchy, regencies (kabupaten) and cities (kota)
 * are at the same level under provinces.
 *
 * @example
 * // Get all regencies in DKI Jakarta province
 * const jakartaRegencies = getRegenciesByProvince('31')
 * // Returns: [{id: '3171', name: 'Jakarta Selatan', ...}, ...]
 */
export function getRegenciesByProvince(provinceId: string) {
  return regencies.filter((regency) => regency.provinceId === provinceId)
}

/**
 * Retrieves all districts (kecamatan) within a specific regency.
 *
 * @param regencyId - The unique identifier of the parent regency
 * @returns Array of districts belonging to the specified regency
 *
 * @remarks
 * Districts (kecamatan) are administrative divisions under regencies/cities.
 *
 * @example
 * // Get all districts in Jakarta Selatan
 * const southJakartaDistricts = getDistrictsByRegency('3171')
 * // Returns: [{id: '317101', name: 'Tebet', ...}, ...]
 */
export function getDistrictsByRegency(regencyId: string) {
  return districts.filter((district) => district.regencyId === regencyId)
}

/**
 * Retrieves all villages (kelurahan/desa) within a specific district.
 *
 * @param districtId - The unique identifier of the parent district
 * @returns Array of villages belonging to the specified district
 *
 * @remarks
 * Villages (kelurahan/desa) are the smallest administrative divisions in Indonesia.
 * Kelurahan are urban villages, while desa are rural villages.
 *
 * @example
 * // Get all villages in Tebet district
 * const tebetVillages = getVillagesByDistrict('317101')
 * // Returns: [{id: '31710101', name: 'Tebet Barat', ...}, ...]
 */
export function getVillagesByDistrict(districtId: string) {
  return villages.filter((village) => village.districtId === districtId)
}

// ============================================================================
// Deprecated aliases for backward compatibility
// ============================================================================

/**
 * @deprecated Use `getProvinces` instead
 */
export function getAllProvinces(_countryId: string) {
  return getProvinces(_countryId)
}

/**
 * @deprecated Use `getRegenciesByProvince` instead
 */
export function getAllRegencies(provinceId: string) {
  return getRegenciesByProvince(provinceId)
}

/**
 * @deprecated Use `getDistrictsByRegency` instead
 */
export function getAllDistricts(regencyId: string) {
  return getDistrictsByRegency(regencyId)
}

/**
 * @deprecated Use `getVillagesByDistrict` instead
 */
export function getAllVillages(districtId: string) {
  return getVillagesByDistrict(districtId)
}
